'use client';

import { SMAA } from '@react-three/postprocessing';
import { EdgeDetectionMode, PredicationMode, SMAAPreset } from 'postprocessing';

export function resolveSmaaPresetId(p: string | undefined): SMAAPreset {
  switch (String(p ?? 'ultra').toLowerCase()) {
    case 'low':
      return SMAAPreset.LOW;
    case 'medium':
      return SMAAPreset.MEDIUM;
    case 'high':
      return SMAAPreset.HIGH;
    default:
      return SMAAPreset.ULTRA;
  }
}

/**
 * Real geometric AA is EffectComposer MSAA. SMAA is a 1-sample morphological
 * cleanup pass — without MSAA underneath it always looks crunchy, and Low/Ultra
 * only tweak that weak filter.
 *
 * Saved scenes used `outputMsaaSamples: 0` (`0 ?? 4` stays 0). Treat 0 as 4×
 * for SMAA. Dedicated MSAA mode still honours an explicit 0.
 */
/** True when Bloom/DOF/SSAO/etc. need linear HDR in the composer (renderer TM off). */
export function postFxNeedsHdrComposer(settings?: {
  enabled?: boolean;
  dof?: { enabled?: boolean };
  bloom?: { enabled?: boolean };
  vignette?: { enabled?: boolean };
  chromaticAberration?: { enabled?: boolean };
  ssao?: { enabled?: boolean };
  sharpenBlur?: { enabled?: boolean };
} | null): boolean {
  if (!settings?.enabled) return false;
  return !!(
    settings.dof?.enabled ||
    settings.bloom?.enabled ||
    settings.vignette?.enabled ||
    settings.chromaticAberration?.enabled ||
    settings.ssao?.enabled ||
    settings.sharpenBlur?.enabled
  );
}

/** True when EffectComposer must own the frame (kills native canvas MSAA). */
export function postFxNeedsComposer(settings?: {
  enabled?: boolean;
  antialiasing?: string;
  dof?: { enabled?: boolean };
  bloom?: { enabled?: boolean };
  vignette?: { enabled?: boolean };
  chromaticAberration?: { enabled?: boolean };
  ssao?: { enabled?: boolean };
  sharpenBlur?: { enabled?: boolean };
} | null): boolean {
  const aa = settings?.antialiasing;
  // SMAA/MSAA on composer: 4× RT MSAA + morphological SMAA (softer than native 4× alone).
  if (aa === 'fxaa' || aa === 'taa' || aa === 'smaa' || aa === 'msaa') return true;
  return postFxNeedsHdrComposer(settings);
}

/** Keep renderer ACES so per-material skipToneMap (Gradient) still works. */
export function postFxUsesRendererToneMap(settings?: {
  enabled?: boolean;
  antialiasing?: string;
  dof?: { enabled?: boolean };
  bloom?: { enabled?: boolean };
  vignette?: { enabled?: boolean };
  chromaticAberration?: { enabled?: boolean };
  ssao?: { enabled?: boolean };
  sharpenBlur?: { enabled?: boolean };
} | null): boolean {
  if (settings?.antialiasing === 'taa') return false;
  return !postFxNeedsHdrComposer(settings);
}

/** Native WebGL `antialias` — only SMAA/MSAA modes, and only when composer does not own the frame. */
export function nativeCanvasMsaa(settings?: {
  enabled?: boolean;
  antialiasing?: string;
  dof?: { enabled?: boolean };
  bloom?: { enabled?: boolean };
  vignette?: { enabled?: boolean };
  chromaticAberration?: { enabled?: boolean };
  ssao?: { enabled?: boolean };
  sharpenBlur?: { enabled?: boolean };
} | null): boolean {
  const aa = settings?.antialiasing;
  if (aa !== 'smaa' && aa !== 'msaa') return false;
  return !postFxNeedsComposer(settings);
}

export function resolveComposerMsaaSamples(
  aa: string | undefined,
  outputMsaaSamples: number | undefined,
): number {
  if (aa !== 'smaa' && aa !== 'msaa') return 0;
  if (aa === 'smaa' && !(outputMsaaSamples && outputMsaaSamples > 0)) return 4;
  if (aa === 'msaa' && outputMsaaSamples === undefined) return 4;
  return Math.max(0, Math.round(outputMsaaSamples ?? 0));
}

/**
 * drei's SMAA wrapper only forwards constructor `args`. Passing `preset` as a
 * React prop never calls applyPreset(). Recreate via key + args.
 */
export function TunableSmaa({ preset: presetName }: { preset?: string }) {
  const preset = resolveSmaaPresetId(presetName);
  return (
    <SMAA
      key={`smaa-${preset}-pred-depth`}
      args={[{
        preset,
        edgeDetectionMode: EdgeDetectionMode.COLOR,
        // Gold cage vs light carpaint is similar luma — COLOR misses those rims.
        // Depth predication still flags the geometric silhouette.
        predicationMode: PredicationMode.DEPTH,
      }]}
    />
  );
}
